// src/features/vita/deeplink/deepLinkHandler.js
//
// Tratador ÚNICO de deep link, compartilhado pelos dois caminhos do app nativo:
//
//   app JÁ ABERTO  → evento `appUrlOpen`
//   app FECHADO    → `App.getLaunchUrl()` no primeiro render (cold start)
//
// Por que os dois são necessários: com `launchMode="singleTask"`, o app fechado
// recebe a URL no INTENT INICIAL (`onCreate`), e o `appUrlOpen` do Capacitor é
// disparado a partir de `onNewIntent` — que não roda nesse caso. Só o listener
// perderia exatamente o clique que ABRIU o app, que é o caso mais comum de quem
// vem do WhatsApp.
//
// E por que o tratador é um módulo puro, e não código dentro do hook: assim os
// dois caminhos usam literalmente a MESMA função (não duas cópias que divergem
// com o tempo), e dá para testar cold start, duplicata e fallback sem montar
// React nem simular o Capacitor.

import {
  DESTINOS,
  HOSTS,
  PREFIXO,
  ROTA_PLATAFORMA,
  abreOChat,
  paraRotaInterna,
  parseDeepLink,
} from "./deepLinkRoutes";

// Janela em que a MESMA URL é considerada repetição do mesmo clique.
//
// Existe porque no iOS o cold start pode disparar `appUrlOpen` **e** devolver a
// URL em `getLaunchUrl()` — sem esta guarda, o app navegaria duas vezes e o
// histórico ficaria com uma entrada fantasma (o "voltar" não voltaria).
// 1,5 s cobre a corrida entre os dois e ainda deixa o aluno reabrir o mesmo
// link de propósito logo em seguida.
export const JANELA_DUPLICATA_MS = 1500;

export const ORIGEM_LISTENER = "listener";
export const ORIGEM_COLD_START = "cold-start";

/**
 * A URL está no nosso namespace de deep link, mesmo que o destino seja
 * desconhecido?
 *
 * Serve para separar dois casos que `parseDeepLink` colapsa em `null`:
 *   - não é link nosso        → ignorar (não somos donos da navegação)
 *   - é `/app/<algo-que-nao-existe>` → o App Link JÁ abriu o app, então parar
 *     em silêncio deixaria o aluno olhando a home sem entender. Cai na
 *     plataforma.
 */
export function ehLinkDoApp(href) {
  if (!href) return false;
  let url;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  if (url.protocol === "lesvita:") return true;
  // O host precisa ser conferido: `getLaunchUrl()` devolve qualquer URL que
  // tenha aberto o app, e sem isto um `https://<outro-host>/app/x` cairia no
  // fallback como se fosse link nosso.
  if (!HOSTS.includes(String(url.hostname || "").toLowerCase())) return false;
  return url.pathname.startsWith(`${PREFIXO}/`);
}

/**
 * Cria o tratador. `navegar(rota)` e `abrirChat()` são injetados para o módulo
 * não conhecer React nem o roteador.
 *
 * Retorna `tratar(href, origem)` → objeto de diagnóstico (usado nos testes e
 * bom para log); nunca lança.
 */
export function criarTratadorDeepLink({ navegar, abrirChat, agora = Date.now }) {
  let ultimoHref = null;
  let ultimoEm = 0;

  return function tratar(href, origem = ORIGEM_LISTENER) {
    if (!href) return { tratado: false, motivo: "sem-url", origem };

    const parsed = parseDeepLink(href);

    if (!parsed) {
      if (!ehLinkDoApp(href)) {
        return { tratado: false, motivo: "nao-e-deeplink", origem };
      }
      // Destino desconhecido dentro do nosso prefixo: fallback, nunca crash.
      const instante = agora();
      if (href === ultimoHref && instante - ultimoEm < JANELA_DUPLICATA_MS) {
        return { tratado: false, motivo: "duplicado", origem };
      }
      ultimoHref = href;
      ultimoEm = instante;
      navegar(ROTA_PLATAFORMA);
      return { tratado: true, fallback: true, rota: ROTA_PLATAFORMA, origem };
    }

    const instante = agora();
    if (href === ultimoHref && instante - ultimoEm < JANELA_DUPLICATA_MS) {
      return { tratado: false, motivo: "duplicado", origem };
    }
    ultimoHref = href;
    ultimoEm = instante;

    const rota = paraRotaInterna(parsed.destino, parsed.params);
    navegar(rota);
    if (abreOChat(parsed.destino)) abrirChat?.();

    return { tratado: true, rota, destino: parsed.destino, origem };
  };
}

export { DESTINOS, ROTA_PLATAFORMA };
